import { useCallback, useRef, type RefObject } from "react";
import type { StandaloneSpeech } from "./use-standalone-speech";
import type { UseElevenLabsAgentReturn } from "./use-elevenlabs-agent";

export type AssistantTransition = "start" | "stop" | null;

export function useAssistantTransitions(input: {
  session: UseElevenLabsAgentReturn;
  speech: StandaloneSpeech;
  transitionRef: RefObject<AssistantTransition>;
}) {
  const { session, speech, transitionRef } = input;
  const startInterruption = useRef<Promise<void> | null>(null);
  const startSession = session.start;
  const stopSession = session.stop;
  const runTransition = useCallback(
    async (kind: Exclude<AssistantTransition, null>, action: () => Promise<void>) => {
      if (speech.isDictating() || transitionRef.current) return;
      transitionRef.current = kind;
      speech.stopReadAloud(true);
      try {
        await action();
      } finally {
        if (transitionRef.current === kind) transitionRef.current = null;
      }
    },
    [speech, transitionRef],
  );
  const start = useCallback(
    () => runTransition("start", startSession),
    [runTransition, startSession],
  );
  const stop = useCallback(() => {
    // End is a cancellation boundary: never transcribe a confirmation while
    // closing its live session.
    if (speech.isDictating()) speech.cancelApprovalCapture();
    if (transitionRef.current !== "start") return runTransition("stop", stopSession);
    speech.stopReadAloud(true);
    if (startInterruption.current) return startInterruption.current;
    const interruption = stopSession().finally(() => {
      if (startInterruption.current === interruption) startInterruption.current = null;
    });
    startInterruption.current = interruption;
    return interruption;
  }, [runTransition, speech, stopSession, transitionRef]);
  return { start, stop };
}
